import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Stack,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Divider
} from '@mui/material';
import {
  DatePicker,
  LocalizationProvider
} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/fr';
import {
  CalendarToday as CalendarIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Pets as PetsIcon
} from '@mui/icons-material';
import { animalService, Animal } from '../../services/animalService';
import rendezVousService, { CreneauDisponible } from '../../services/rendezvousService';
import { userService } from '../../services/userService';
import { useAuth } from '../../contexts/AuthContext';

// Interface pour les utilisateurs (vétérinaires)
interface User {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
}

// Utilitaire pour extraire l'heure d'un CreneauDisponible
const getCreneauHeure = (creneau: CreneauDisponible): string => {
  return dayjs(creneau.date_heure).format('HH:mm');
};

// Service pour récupérer les vétérinaires depuis l'API
const getVeterinaires = async (): Promise<{ results: User[] }> => {
  try {
    const response = await userService.getVeterinaires();
    return response;
  } catch (error) {
    console.error('Erreur lors du chargement des vétérinaires:', error);
    // Fallback en cas d'erreur
    return { results: [] };
  }
};

interface PrendreRendezVousProps {
  onSuccess: () => void;
  onCancel: () => void;
}

const PrendreRendezVous: React.FC<PrendreRendezVousProps> = ({
  onSuccess,
  onCancel
}) => {
  const { user } = useAuth();
  
  // États pour la navigation des étapes
  const [activeStep, setActiveStep] = useState(0);

  // États pour le formulaire
  const [selectedVeterinaire, setSelectedVeterinaire] = useState<number | ''>('');
  const [selectedAnimal, setSelectedAnimal] = useState<number | ''>('');
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [selectedCreneau, setSelectedCreneau] = useState<CreneauDisponible | null>(null);
  const [motif, setMotif] = useState('');

  // États pour les données et le chargement
  const [creneauxDisponibles, setCreneauxDisponibles] = useState<CreneauDisponible[]>([]);
  const [loadingCreneaux, setLoadingCreneaux] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [veterinaires, setVeterinaires] = useState<User[]>([]);
  const [animaux, setAnimaux] = useState<Animal[]>([]);

  const steps = [
    'Sélection du vétérinaire et de l\'animal',
    'Choix de la date',
    'Sélection du créneau et motif',
    'Confirmation'
  ];

  const loadInitialData = useCallback(async () => {
    setLoadingCreneaux(true);
    try {
      const [vetsResponse, animauxResponse] = await Promise.all([
        getVeterinaires(),
        animalService.getMyAnimals()
      ]);
      
      setVeterinaires(vetsResponse.results);
      setAnimaux(animauxResponse.results);
      
      // Présélectionner le vétérinaire de l'utilisateur ou le premier disponible
      if (user?.veterinaire_selectionne && vetsResponse.results.length > 0) {
        // Trouver le vétérinaire correspondant au vétérinaire sélectionné de l'utilisateur
        const selectedVet = vetsResponse.results.find(vet => vet.id === user.veterinaire_selectionne);
        if (selectedVet && selectedVet.id) {
          setSelectedVeterinaire(selectedVet.id);
        } else if (vetsResponse.results.length > 0 && vetsResponse.results[0].id) {
          setSelectedVeterinaire(vetsResponse.results[0].id);
        }
      } else if (vetsResponse.results.length > 0 && vetsResponse.results[0].id) {
        setSelectedVeterinaire(vetsResponse.results[0].id);
      }
      
      // Sélectionner automatiquement le premier animal s'il existe
      if (animauxResponse.results.length > 0 && animauxResponse.results[0].id) {
        setSelectedAnimal(animauxResponse.results[0].id);
      }
      
      // Ne pas charger les créneaux automatiquement car pas de date sélectionnée
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des données');
      console.error('Erreur:', err);
    } finally {
      setLoadingCreneaux(false);
    }
  }, [user]);

  // Charger les données initiales
  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const loadCreneauxDisponibles = useCallback(async () => {
    if (!selectedDate || !selectedVeterinaire) return;

    setLoadingCreneaux(true);
    try {
      const dateStr = selectedDate.format('YYYY-MM-DD');
      const creneaux = await rendezVousService.getCreneauxDisponibles(
        selectedVeterinaire as number,
        dateStr
      );
      setCreneauxDisponibles(creneaux);
    } catch (error) {
      console.error('Erreur lors du chargement des créneaux:', error);
      setError('Erreur lors du chargement des créneaux disponibles');
    } finally {
      setLoadingCreneaux(false);
    }
  }, [selectedDate, selectedVeterinaire]);

  // Charger les créneaux disponibles quand la date et le vétérinaire sont sélectionnés
  useEffect(() => {
    if (selectedDate && selectedVeterinaire) {
      loadCreneauxDisponibles();
    } else {
      setCreneauxDisponibles([]);
    }
  }, [selectedDate, selectedVeterinaire, loadCreneauxDisponibles]);

  const handleNext = () => {
    const newActiveStep = activeStep + 1;
    setActiveStep(newActiveStep);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStepClick = (step: number) => {
    setActiveStep(step);
  };

  const handleSubmit = async () => {
    if (!selectedVeterinaire || !selectedAnimal || !selectedDate || !selectedCreneau || !motif.trim()) {
      setError('Veuillez remplir tous les champs');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const dateTime = dayjs(selectedCreneau.date_heure);
      
      await rendezVousService.createRendezVous({
        veterinaire: selectedVeterinaire as number,
        animal: selectedAnimal as number,
        date_heure: dateTime.toISOString(),
        motif: motif.trim()
      });

      setSuccess('Rendez-vous demandé avec succès !');
      
      setTimeout(() => {
        onSuccess();
      }, 2000);

    } catch (error) {
      console.error('Erreur lors de la création du rendez-vous:', error);
      setError('Erreur lors de la demande de rendez-vous');
    } finally {
      setSubmitting(false);
    }
  };

  const canProceedToStep = (step: number) => {
    switch (step) {
      case 1:
        return selectedVeterinaire !== '' && selectedAnimal !== '';
      case 2:
        return selectedDate !== null;
      case 3:
        return selectedCreneau !== null && motif.trim() !== '';
      default:
        return true;
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Stack spacing={3}>
            <FormControl fullWidth>
              <InputLabel>Vétérinaire</InputLabel>
              <Select
                value={selectedVeterinaire}
                onChange={(e) => setSelectedVeterinaire(e.target.value)}
                label="Vétérinaire"
              >
                {veterinaires.map((vet) => (
                  <MenuItem key={vet.id} value={vet.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon fontSize="small" />
                      Dr. {vet.first_name} {vet.last_name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Animal</InputLabel>
              <Select
                value={selectedAnimal}
                onChange={(e) => setSelectedAnimal(e.target.value)}
                label="Animal"
              >
                {animaux.map((animal) => (
                  <MenuItem key={animal.id} value={animal.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PetsIcon fontSize="small" />
                      {animal.nom} ({animal.espece})
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        );

      case 1:
        return (
          <Stack spacing={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CalendarIcon color="primary" />
              <Typography variant="h6">Choisissez une date</Typography>
            </Box>
            
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Date du rendez-vous"
                value={selectedDate}
                onChange={(newDate) => {
                  if (newDate) {
                    setSelectedDate(dayjs(newDate));
                  } else {
                    setSelectedDate(null);
                  }
                }}
                shouldDisableDate={(date) => {
                  const dayjsDate = dayjs(date);
                  return dayjsDate.day() === 0 || dayjsDate.day() === 6; // 0 = dimanche, 6 = samedi
                }}
                minDate={dayjs()}
                format="DD/MM/YYYY"
                sx={{ width: '100%' }}
              />
            </LocalizationProvider>

            <Alert severity="info">
              Les consultations ont lieu du lundi au vendredi uniquement.
            </Alert>
          </Stack>
        );

      case 2:
        return (
          <Stack spacing={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <ScheduleIcon color="primary" />
              <Typography variant="h6">Créneaux disponibles</Typography>
            </Box>

            {loadingCreneaux ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress />
              </Box>
            ) : creneauxDisponibles.length > 0 ? (
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  Sélectionnez un créneau horaire :
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {creneauxDisponibles.map((creneau) => (
                    <Chip
                      key={getCreneauHeure(creneau)}
                      label={getCreneauHeure(creneau)}
                      onClick={() => setSelectedCreneau(creneau)}
                      color={selectedCreneau && getCreneauHeure(selectedCreneau) === getCreneauHeure(creneau) ? 'primary' : 'default'}
                      variant={selectedCreneau && getCreneauHeure(selectedCreneau) === getCreneauHeure(creneau) ? 'filled' : 'outlined'}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              </Stack>
            ) : (
              <Alert severity="warning">
                Aucun créneau disponible pour cette date. Veuillez choisir une autre date.
              </Alert>
            )}

            <Divider />

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Motif de la consultation"
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
              placeholder="Décrivez brièvement le motif de votre visite..."
              required
            />
          </Stack>
        );

      case 3:
        const selectedVet = veterinaires.find(v => v.id === selectedVeterinaire);
        const selectedAnimalObj = animaux.find(a => a.id === selectedAnimal);
        
        return (
          <Stack spacing={3}>
            <Typography variant="h6" gutterBottom>
              Récapitulatif de votre rendez-vous
            </Typography>

            <Card variant="outlined">
              <CardContent>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" color="primary">Vétérinaire</Typography>
                    <Typography>Dr. {selectedVet?.first_name} {selectedVet?.last_name}</Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="primary">Animal</Typography>
                    <Typography>{selectedAnimalObj?.nom} ({selectedAnimalObj?.espece})</Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="primary">Date et heure</Typography>
                    <Typography>
                      {selectedDate?.format('dddd DD MMMM YYYY')} à {selectedCreneau ? getCreneauHeure(selectedCreneau) : ''}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="primary">Motif</Typography>
                    <Typography>{motif}</Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            {success && (
              <Alert severity="success">
                {success}
              </Alert>
            )}
          </Stack>
        );

      default:
        return 'Étape inconnue';
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ width: '100%', maxWidth: 800, mx: 'auto', p: 3 }}>
        <Typography variant="h4" gutterBottom align="center">
          Prendre rendez-vous
        </Typography>

        <Stack direction="row" spacing={2} justifyContent="center" mb={4}>
          {steps.map((step, index) => (
            <Chip
              key={step}
              label={step}
              color={activeStep === index ? 'primary' : 'default'}
              variant={activeStep === index ? 'filled' : 'outlined'}
              onClick={() => handleStepClick(index)}
              sx={{ cursor: 'pointer' }}
            />
          ))}
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Card>
          <CardContent>
            {renderStepContent(activeStep)}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button
                onClick={handleBack}
                disabled={activeStep === 0}
              >
                Précédent
              </Button>

              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={submitting || !canProceedToStep(activeStep)}
                >
                  {submitting ? <CircularProgress size={24} /> : 'Confirmer le rendez-vous'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={!canProceedToStep(activeStep + 1)}
                >
                  Suivant
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </LocalizationProvider>
  );
};

export default PrendreRendezVous; 